import { type ReactNode, useCallback, useState } from "react";

import { ButtonIcon } from "@backstage/ui";
import { RiSidebarFoldLine, RiSidebarUnfoldLine } from "@remixicon/react";

import { SidebarOpenStateProvider } from "./sidebarOpenState";
import { useSidebarPinState } from "./sidebarPinState";
import styles from "./SidebarRail.module.css";

/**
 * The desktop nav rail: a fixed 72px column that expands to 224px.
 *
 * Replaces `Sidebar` from `@backstage/core-components` and reproduces its two
 * interaction modes:
 *
 * - **Hover / focus to peek.** While unpinned, pointing at or tabbing into the
 *   rail expands it as an overlay. Content underneath does not move, because
 *   `AppLayout` only pads for the *pinned* width.
 * - **Pin to keep open.** Pinning expands the rail and shifts page content, and
 *   the choice is persisted. Pin state is owned by `AppLayout` and read here
 *   through `useSidebarPinState`, so the rail and the content offset can never
 *   disagree.
 *
 * The original expanded on a timer (600ms in, 0ms out). That delay existed to
 * stop the rail flickering as the pointer crossed it; CSS transitions handle
 * that more cheaply here, so the timers are gone.
 */
export const SidebarRail = (props: { children: ReactNode }) => {
    const { children } = props;
    const { isPinned, toggleSidebarPinState } = useSidebarPinState();
    const [isPeeking, setIsPeeking] = useState(false);

    const isOpen = isPinned || isPeeking;

    /*
     * Unpinning has to clear the peek state as well as the pin.
     *
     * The toggle can only be reached by pointing at or tabbing into the rail,
     * which is exactly what sets `isPeeking`. Toggling the pin alone would
     * therefore leave the rail open and the button looking inert — the user
     * would have to move the pointer away before anything happened.
     */
    const togglePinned = useCallback(() => {
        setIsPeeking(false);
        toggleSidebarPinState();
    }, [toggleSidebarPinState]);

    return (
        <nav
            // Not "Main navigation": a `<nav>` is already announced as a
            // navigation landmark, so that name reads as "Main navigation
            // navigation".
            aria-label="Main"
            data-open={isOpen}
            data-testid="sidebar-rail"
            className={styles.root}
            onMouseEnter={() => setIsPeeking(true)}
            onMouseLeave={() => setIsPeeking(false)}
            onFocus={() => setIsPeeking(true)}
            onBlur={event => {
                // Only collapse once focus has left the rail entirely;
                // `blur` also fires when moving between items inside it.
                if (!event.currentTarget.contains(event.relatedTarget)) {
                    setIsPeeking(false);
                }
            }}
        >
            <SidebarOpenStateProvider isOpen={isOpen}>
                {children}
                {/*
                 * Rendered in both states rather than only when open. Hiding
                 * it while collapsed unmounted the very button the user had
                 * just activated, throwing keyboard focus back to the body,
                 * and left no way to re-pin without hovering first.
                 */}
                <div className={styles.pinToggle} data-open={isOpen}>
                    <ButtonIcon
                        size="small"
                        variant="tertiary"
                        className={styles.pinToggleButton}
                        onPress={togglePinned}
                        aria-label={
                            isPinned ? "Unpin sidebar" : "Pin sidebar open"
                        }
                        icon={
                            isPinned ? (
                                <RiSidebarFoldLine size={16} aria-hidden />
                            ) : (
                                <RiSidebarUnfoldLine size={16} aria-hidden />
                            )
                        }
                    />
                </div>
            </SidebarOpenStateProvider>
        </nav>
    );
};
